// src/pages/admin/GameEditPage.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL
} from 'firebase/storage';
import { db } from '../../firebase';
import GameBindingEdit from '../../components/games/edit/GameBindingEdit';

export default function GameEditPage() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);

  // ==============================
  // convertToWebp: PNG/JPEG 등 이미지를 WebP Blob으로 변환
  // ==============================
  const convertToWebp = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error('WebP 변환 실패'));
              }
            },
            'image/webp',
            1.0 // 품질: 0.0 ~ 1.0 (0.8 권장)
          );
        };
        img.onerror = () => reject(new Error('이미지 로딩 실패'));
        img.src = reader.result;
      };
      reader.onerror = () => reject(new Error('FileReader 실패'));
      reader.readAsDataURL(file);
    });
  };

  // ==============================
  // uploadFileWithCache: Blob/File 객체 업로드 + Cache-Control 설정
  // ==============================
  const uploadFileWithCache = async (blobOrFile, folderPath, filename, contentType) => {
    const storage = getStorage();
    const fileRef = storageRef(storage, `${folderPath}/${filename}`);
    const metadata = {
      contentType,
      cacheControl: 'public, max-age=43200' // 12시간 캐시
    };
    await uploadBytes(fileRef, blobOrFile, metadata);
    const url = await getDownloadURL(fileRef);
    return url;
  };

  useEffect(() => {
    (async () => {
      try {
        const ref = doc(db, 'games', gameId);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          setGame({ id: snap.id, ...snap.data() });
        } else {
          alert('해당 게임을 찾을 수 없습니다.');
          navigate('/admin', { state: { tab: 'games' } });
        }
      } catch {
        alert('불러오는 중 오류가 발생했습니다.');
        navigate('/admin', { state: { tab: 'games' } });
      } finally {
        setLoading(false);
      }
    })();
  }, [gameId, navigate]);

  const handleSubmit = async (data) => {
    try {
      // 1) 캡슐 이미지 업로드: PNG/JPEG → WebP로 변환 후 업로드 (games/{gameId} 폴더, 확장자 없이 저장)
      const capsuleUrls = await Promise.all(
        (data.capsuleFiles || []).map(async (file, i) => {
          if (file) {
            const webpBlob = await convertToWebp(file);
            const webpFilename = `${gameId}_capsule_${i}`;
            const url = await uploadFileWithCache(
              webpBlob,
              `games/${gameId}`,
              webpFilename,
              'image/webp'
            );
            return url;
          }
          return Promise.resolve(data.capsulePreviews[i] || null);
        })
      );

      // 2) media 업로드: 이미지인 경우 WebP로 변환, 기타 파일은 원본 그대로 업로드 (games/{gameId} 폴더, 확장자 없이 저장)
      const mediaItems = await Promise.all(
        (data.media || []).map(async (item, i) => {
          let url;
          let finalType = item.type;
          const filenameBase = `${gameId}_media_${i}`;

          if (item.file) {
            const file = item.file;
            if (file.type.startsWith('image/')) {
              const webpBlob = await convertToWebp(file);
              const webpFilename = filenameBase;
              url = await uploadFileWithCache(
                webpBlob,
                `games/${gameId}`,
                webpFilename,
                'image/webp'
              );
              finalType = 'image/webp';
            } else {
              url = await uploadFileWithCache(
                file,
                `games/${gameId}`,
                filenameBase,
                file.type
              );
              finalType = file.type;
            }
          } else {
            url = item.url;
          }

          return { type: finalType, url };
        })
      );

      // 3) Firestore에 쓸 페이로드 구성
      const payload = {
        id: gameId,
        title: data.title,
        capsuleImageUrl: capsuleUrls,
        shortDescription: data.shortDescription,
        longDescription: data.longDescription,
        releaseDate: data.releaseDate,
        comingSoon: data.comingSoon,
        genres: data.genres,
        tags: data.tags,
        developer: data.developer,
        publisher: data.publisher,
        rating: data.rating,
        languages: data.languages,
        links: data.links,
        media: mediaItems,
        price: data.price,
        discountRate: data.discountRate,
        systemRequirement: data.systemRequirement,
      };

      // 4) Firestore 덮어쓰기
      await setDoc(doc(db, 'games', gameId), payload);
      alert('게임 수정 완료!');
      navigate('/admin', { state: { tab: 'games' } });
    } catch (error) {
      console.error('업로드 중 오류 발생:', error);
      alert('저장 중 오류가 발생했습니다. 다시 시도해주세요.');
    }
  };

  if (loading) return <div className="p-6">로딩 중…</div>;

  return <GameBindingEdit game={game} gameId={gameId} onSubmit={handleSubmit} />;
}
